import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { dataService, Registration } from '../services/dataService';
import { ConfirmationDialog, DashboardHeader } from '../components';
import '../styles/dashboard.css';

interface GroupedEvent {
  id: string;
  eventId: string;
  eventName: string;
  status: string;
}

interface GroupedStudent {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  ageGroup: string;
  registrationDate: string;
  events: GroupedEvent[];
}

const CoachDashboard: React.FC = () => {
  const { user, registrations, loadRegistrations, deleteRegistration, isLoading, isRegistrationOpen } = useApp();
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  });
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<GroupedStudent | null>(null);

  // Name editing state
  const [editedFirstName, setEditedFirstName] = useState('');
  const [editedLastName, setEditedLastName] = useState('');
  const [isUpdatingName, setIsUpdatingName] = useState(false);

  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [eventToDelete, setEventToDelete] = useState<string>('');
  
  // Student deletion state for Grade 7
  const [showStudentDeleteDialog, setShowStudentDeleteDialog] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<GroupedStudent | null>(null);

  // Add event state
  const [showAddEventDialog, setShowAddEventDialog] = useState(false);
  const [availableEvents, setAvailableEvents] = useState<any[]>([]);
  const [studentForEventAdd, setStudentForEventAdd] = useState<GroupedStudent | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [eventFilter, setEventFilter] = useState('all');
  const [ageGroupFilter, setAgeGroupFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Mobile filters state
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Mobile guidelines state
  const [showMobileGuidelines, setShowMobileGuidelines] = useState(false);

  const loadCoachData = async () => {
    if (!user?.schoolId) return;

    try {
      // Load registrations for this coach's school
      await loadRegistrations({
        schoolId: user.schoolId,
        coachId: user.id
      });
    } catch (error) {
      console.error('Failed to load coach data:', error);
    }
  };

  useEffect(() => {
    if (user?.schoolId) {
      loadCoachData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, eventFilter, ageGroupFilter, statusFilter]);

  // Update selected student when registrations change (for live updates after adding events)
  useEffect(() => {
    if (showEditDialog && selectedStudent && registrations.length > 0) {
      const updatedStudents = groupRegistrationsByStudent(registrations);
      const updatedStudent = updatedStudents.find(s => 
        s.firstName === selectedStudent.firstName &&
        s.lastName === selectedStudent.lastName &&
        s.dateOfBirth === selectedStudent.dateOfBirth &&
        s.gender === selectedStudent.gender
      );
      if (updatedStudent) {
        setSelectedStudent(updatedStudent);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrations, showEditDialog]);

  useEffect(() => {
    // Calculate stats when registrations change
    const newStats = {
      total: registrations.length,
      pending: registrations.filter(r => r.status === 'pending' || r.status === null).length,
      approved: registrations.filter(r => r.status === 'approved').length,
      rejected: registrations.filter(r => r.status === 'rejected').length
    };
    setStats(newStats);
  }, [registrations]);

  const handleDeleteRegistration = async (registrationId: string, eventName?: string) => {
    // Set up the confirmation dialog
    setPendingDeleteId(registrationId);
    setEventToDelete(eventName || 'this event');
    setShowConfirmDialog(true);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;

    try {
      await deleteRegistration(pendingDeleteId);
      
      // Update the selectedStudent to remove the deleted event from the UI
      if (selectedStudent) {
        const updatedEvents = selectedStudent.events.filter(event => event.id !== pendingDeleteId);
        setSelectedStudent({
          ...selectedStudent,
          events: updatedEvents
        });
      }
      
    } catch (error) {
      console.error('Failed to delete registration:', error);
    } finally {
      // Reset confirmation dialog state
      setShowConfirmDialog(false);
      setPendingDeleteId(null);
      setEventToDelete('');
    }
  };

  const cancelDelete = () => {
    setShowConfirmDialog(false);
    setPendingDeleteId(null);
    setEventToDelete('');
  };

  // Grade 7 student deletion functions
  const handleDeleteStudent = (student: GroupedStudent) => {
    setStudentToDelete(student);
    setShowStudentDeleteDialog(true);
  };

  const confirmStudentDelete = async () => {
    if (!studentToDelete) return;

    try {
      // Delete all events for this student (soft delete)
      await Promise.all(
        studentToDelete.events.map(event => deleteRegistration(event.id))
      );
      
      // Close the edit dialog and refresh data
      setShowEditDialog(false);
      setSelectedStudent(null);
      await loadCoachData();
      
    } catch (error) {
      console.error('Failed to delete student events:', error);
    } finally {
      // Reset confirmation dialog state
      setShowStudentDeleteDialog(false);
      setStudentToDelete(null);
    }
  };

  const cancelStudentDelete = () => {
    setShowStudentDeleteDialog(false);
    setStudentToDelete(null);
  };

  const handleEditStudent = (student: GroupedStudent) => {
    setSelectedStudent(student);
    setEditedFirstName(student.firstName);
    setEditedLastName(student.lastName);
    setShowEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setShowEditDialog(false);
    setSelectedStudent(null);
    setEditedFirstName('');
    setEditedLastName('');
  };

  const handleSaveStudentName = async () => {
    if (!selectedStudent) return;

    try {
      setIsUpdatingName(true);

      // Validate input
      if (!editedFirstName.trim() || !editedLastName.trim()) {
        alert('First name and last name are required');
        return;
      }

      // Update the student name for all their registrations
      const updatePromises = selectedStudent.events.map(event => 
        dataService.updateStudentName(
          event.id,
          editedFirstName.trim(),
          editedLastName.trim()
        )
      );

      await Promise.all(updatePromises);

      // Refresh registrations to get updated data
      await loadRegistrations();

      // Show success message
      alert('Student name updated successfully');

    } catch (error) {
      console.error('Error updating student name:', error);
      alert('Failed to update student name. Please try again.');
    } finally {
      setIsUpdatingName(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getEventName = (eventId: string) => {
    const eventMap: { [key: string]: string } = {
      '100m': '100m Sprint',
      '200m': '200m Sprint',
      '400m': '400m Race',
      '800m': '800m Race',
      '1500m': '1500m Race',
      'shot_put': 'Shot Put',
      'high_jump': 'High Jump',
      'javelin': 'Javelin Throw',
      'discus': 'Discus Throw',
      'long_jump': 'Long Jump',
      '4x100_relay': '4×100m Relay'
    };
    return eventMap[eventId] || eventId;
  };

  const getAgeGroupName = (ageGroupId: string) => {
    const ageGroupMap: { [key: string]: string } = {
      'under_12': 'Under 12',
      'under_14': 'Under 14',
      'under_16': 'Under 16',
      'under_18': 'Under 18',
      'open': 'Open',
      'Grade 7': 'Grade 7',
      'Under 15': 'Under 15',
      'Under 17': 'Under 17', 
      'Under 20': 'Under 20'
    };
    return ageGroupMap[ageGroupId] || ageGroupId;
  };

  // Check if student has been reviewed by headmaster (has approved/rejected events)
  const hasBeenReviewed = (student: GroupedStudent) => {
    return student.events.some(event => 
      event.status === 'approved' || event.status === 'rejected'
    );
  };

  // Get available events for a student based on age group and gender
  const getAvailableEventsForStudent = async (student: GroupedStudent) => {
    try {
      // Get all events
      const allEvents = await dataService.getEvents();
      
      // Get current registrations for this school (fresh data)
      const allRegistrations = await dataService.getRegistrations({ schoolId: user?.schoolId });
      
      // Get student's current registrations (including the ones just added)
      const studentRegistrations = allRegistrations.filter(reg => 
        reg.first_name === student.firstName &&
        reg.last_name === student.lastName &&
        reg.date_of_birth === student.dateOfBirth &&
        reg.gender === student.gender &&
        !reg.deleted_at // Exclude soft deleted registrations
      );
      
      const studentEventIds = studentRegistrations.map(reg => reg.sport);
      
      // Filter out events the student is already registered for
      const availableEvents = allEvents.filter(event => 
        !studentEventIds.includes(event.id)
      );

      // Filter events that haven't reached the 3-student limit and add registration count
      const filteredEventsWithCounts = availableEvents.map(event => {
        const eventRegistrations = allRegistrations.filter(reg => 
          reg.sport === event.id && 
          reg.age_group === student.ageGroup && 
          reg.gender === student.gender &&
          !reg.deleted_at // Exclude soft deleted registrations
        );
        return {
          ...event,
          registrationCount: eventRegistrations.length
        };
      }).filter(event => event.registrationCount < 3);

      return filteredEventsWithCounts;
    } catch (error) {
      console.error('Error getting available events:', error);
      return [];
    }
  };

  // Handle showing add event dialog
  const handleAddEvent = async (student: GroupedStudent) => {
    // Double check that student has fewer than 3 events, is not Grade 7, and hasn't been reviewed
    if (student.ageGroup === 'Grade 7' || student.events.length >= 3 || hasBeenReviewed(student)) {
      return;
    }
    
    setStudentForEventAdd(student);
    const events = await getAvailableEventsForStudent(student);
    setAvailableEvents(events);
    setShowAddEventDialog(true);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm('');
    setEventFilter('all');
    setAgeGroupFilter('all');
    setStatusFilter('all');
    setCurrentPage(1);
  };

  // Handle mobile filters
  const handleMobileFiltersClose = () => {
    setShowMobileFilters(false);
  };

  // Handle mobile guidelines
  const handleMobileGuidelinesClose = () => {
    setShowMobileGuidelines(false);
  };

  // Handle adding a new event to student
  const handleConfirmAddEvent = async (eventId: string) => {
    if (!studentForEventAdd || !user?.schoolId) return;

    try {
      const newRegistration = {
        school_id: user.schoolId,
        first_name: studentForEventAdd.firstName,
        last_name: studentForEventAdd.lastName,
        date_of_birth: studentForEventAdd.dateOfBirth,
        gender: studentForEventAdd.gender as 'male' | 'female',
        sport: eventId,
        age_group: studentForEventAdd.ageGroup,
        status: 'pending' as const,
        created_by: user.id
      };

      await dataService.createRegistration(newRegistration);
      
      // Reload data and wait for it to complete
      await loadRegistrations({
        schoolId: user.schoolId,
        coachId: user.id
      });

      // Close the dialog
      setShowAddEventDialog(false);
      setStudentForEventAdd(null);
      setAvailableEvents([]);

      // The selected student will be updated via the useEffect that watches registrations
    } catch (error) {
      console.error('Error adding event:', error);
    }
  };

  // Group registrations by student
  const groupRegistrationsByStudent = (registrations: Registration[]): GroupedStudent[] => {
    const grouped = new Map<string, GroupedStudent>();
    
    registrations.forEach(registration => {
      const studentKey = `${registration.firstName || ''}_${registration.lastName || ''}_${registration.date_of_birth || registration.dateOfBirth || ''}_${registration.gender || ''}`;
      
      if (grouped.has(studentKey)) {
        // Add event to existing student
        const existingStudent = grouped.get(studentKey);
        if (existingStudent) {
          existingStudent.events.push({
            id: registration.id || '',
            eventId: registration.sport || registration.eventId || '',
            eventName: getEventName(registration.sport || registration.eventId || ''),
            status: registration.status || 'unknown'
          });
        }
      } else {
        // Create new student entry
        grouped.set(studentKey, {
          firstName: registration.firstName || '',
          lastName: registration.lastName || '',
          dateOfBirth: registration.date_of_birth || registration.dateOfBirth || '',
          gender: registration.gender || '',
          ageGroup: registration.age_group || registration.ageGroupId || '',
          registrationDate: registration.created_at?.split('T')[0] || registration.registrationDate || '',
          events: [{
            id: registration.id || '',
            eventId: registration.sport || registration.eventId || '',
            eventName: getEventName(registration.sport || registration.eventId || ''),
            status: registration.status || 'unknown'
          }]
        });
      }
    });
    
    return Array.from(grouped.values());
  };

  // Filter students based on search and filter criteria
  const filterStudents = (students: GroupedStudent[]): GroupedStudent[] => {
    return students.filter(student => {
      // Search filter (by name)
      const matchesSearch = searchTerm === '' || 
        `${student.firstName} ${student.lastName}`.toLowerCase().includes(searchTerm.toLowerCase());

      // Event filter
      const matchesEvent = eventFilter === 'all' || 
        student.events.some(event => event.eventId === eventFilter);

      // Age group filter
      const matchesAgeGroup = ageGroupFilter === 'all' || 
        student.ageGroup === ageGroupFilter;

      // Status filter
      const matchesStatus = statusFilter === 'all' || 
        student.events.some(event => event.status === statusFilter);

      return matchesSearch && matchesEvent && matchesAgeGroup && matchesStatus;
    });
  };

  const groupedStudents = groupRegistrationsByStudent(registrations);
  const filteredStudents = filterStudents(groupedStudents);

  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0A0E17 0%, #111827 50%, #0A0E17 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2rem'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '3px solid rgba(212, 175, 55, 0.2)',
          borderTop: '3px solid #FFD700',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{
            margin: 0,
            fontSize: '1.5rem',
            fontFamily: "'Bebas Neue', sans-serif",
            letterSpacing: '2px',
            background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            LOADING DASHBOARD
          </h2>
          <p style={{ margin: '0.5rem 0 0 0', color: '#9CA3AF', fontSize: '0.9rem' }}>
            Please wait...
          </p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <DashboardHeader />
      <div className="dashboard-content-with-header" style={{ 
        background: 'linear-gradient(135deg, #0A0E17 0%, #111827 50%, #0A0E17 100%)', 
        minHeight: '100vh',
        padding: '2rem 1rem',
        paddingTop: '100px'
      }}>
        <style>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Page Title Section */}
          <div style={{ 
            marginBottom: '2rem',
            padding: '2rem',
            background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(26, 31, 46, 0.9) 100%)',
            borderRadius: '20px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(212, 175, 55, 0.15)',
            position: 'relative',
            overflow: 'hidden',
            animation: 'fadeInUp 0.6s ease-out'
          }}>
            <div>
              <h1 style={{ 
                margin: 0,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '700',
                fontFamily: "'Bebas Neue', sans-serif",
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FFD700 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                lineHeight: '1.2'
              }}>
                Welcome, {user?.firstName || 'Coach'}
              </h1>
              <p style={{ 
                color: '#9CA3AF', 
                fontSize: '1rem', 
                margin: '0.5rem 0 0 0',
                lineHeight: '1.5'
              }}>
                Manage your school's athletics registrations
              </p>
            </div>
          </div>

        {/* Statistics Cards */}
        <div className="stats-grid" style={{ 
          display: 'grid', 
          gridTemplateColumns: window.innerWidth <= 768 ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(220px, 1fr))', 
          gap: window.innerWidth <= 768 ? '0.75rem' : '1.5rem', 
          marginBottom: window.innerWidth <= 768 ? '1.5rem' : '2rem' 
        }}>
          {/* Total Registrations */}
          <div style={{ 
            background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)',
            borderRadius: window.innerWidth <= 768 ? '12px' : '16px',
            padding: window.innerWidth <= 768 ? '1rem' : '1.5rem',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            animation: 'fadeInUp 0.5s ease-out 0.1s both'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 12px 40px rgba(59, 130, 246, 0.2)';
            e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.4)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
            e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.2)';
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ 
                  fontSize: window.innerWidth <= 768 ? '2rem' : '2.5rem', 
                  fontWeight: '700', 
                  color: '#3b82f6',
                  lineHeight: '1',
                  fontFamily: "'Bebas Neue', sans-serif",
                  letterSpacing: '1px'
                }}>
                  {stats.total}
                </div>
                <div style={{ 
                  fontSize: window.innerWidth <= 768 ? '0.7rem' : '0.85rem', 
                  fontWeight: '600', 
                  color: '#9CA3AF',
                  textTransform: 'uppercase', 
                  letterSpacing: '0.5px',
                  marginTop: '0.5rem'
                }}>
                  Total Registrations
                </div>
              </div>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(59, 130, 246, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Pending Review */}
          <div style={{ 
            background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)',
            borderRadius: window.innerWidth <= 768 ? '12px' : '16px',
            padding: window.innerWidth <= 768 ? '1rem' : '1.5rem',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(212, 175, 55, 0.2)',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            animation: 'fadeInUp 0.5s ease-out 0.2s both'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 12px 40px rgba(212, 175, 55, 0.2)';
            e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.4)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
            e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.2)';
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ 
                  fontSize: window.innerWidth <= 768 ? '2rem' : '2.5rem', 
                  fontWeight: '700', 
                  color: '#FFD700',
                  lineHeight: '1',
                  fontFamily: "'Bebas Neue', sans-serif",
                  letterSpacing: '1px'
                }}>
                  {stats.pending}
                </div>
                <div style={{ 
                  fontSize: window.innerWidth <= 768 ? '0.7rem' : '0.85rem', 
                  fontWeight: '600', 
                  color: '#9CA3AF',
                  textTransform: 'uppercase', 
                  letterSpacing: '0.5px',
                  marginTop: '0.5rem'
                }}>
                  Pending Review
                </div>
              </div>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(212, 175, 55, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FFD700" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12,6 12,12 16,14"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Approved */}
          <div style={{ 
            background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)',
            borderRadius: window.innerWidth <= 768 ? '12px' : '16px',
            padding: window.innerWidth <= 768 ? '1rem' : '1.5rem',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(46, 139, 87, 0.2)',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            animation: 'fadeInUp 0.5s ease-out 0.3s both'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 12px 40px rgba(46, 139, 87, 0.2)';
            e.currentTarget.style.borderColor = 'rgba(46, 139, 87, 0.4)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
            e.currentTarget.style.borderColor = 'rgba(46, 139, 87, 0.2)';
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ 
                  fontSize: window.innerWidth <= 768 ? '2rem' : '2.5rem', 
                  fontWeight: '700', 
                  color: '#2E8B57',
                  lineHeight: '1',
                  fontFamily: "'Bebas Neue', sans-serif",
                  letterSpacing: '1px'
                }}>
                  {stats.approved}
                </div>
                <div style={{ 
                  fontSize: window.innerWidth <= 768 ? '0.7rem' : '0.85rem', 
                  fontWeight: '600', 
                  color: '#9CA3AF',
                  textTransform: 'uppercase', 
                  letterSpacing: '0.5px',
                  marginTop: '0.5rem'
                }}>
                  Approved
                </div>
              </div>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(46, 139, 87, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2E8B57" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22,4 12,14.01 9,11.01"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Rejected */}
          <div style={{ 
            background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)',
            borderRadius: window.innerWidth <= 768 ? '12px' : '16px',
            padding: window.innerWidth <= 768 ? '1rem' : '1.5rem',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(196, 30, 58, 0.2)',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            animation: 'fadeInUp 0.5s ease-out 0.4s both'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 12px 40px rgba(196, 30, 58, 0.2)';
            e.currentTarget.style.borderColor = 'rgba(196, 30, 58, 0.4)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
            e.currentTarget.style.borderColor = 'rgba(196, 30, 58, 0.2)';
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ 
                  fontSize: window.innerWidth <= 768 ? '2rem' : '2.5rem', 
                  fontWeight: '700', 
                  color: '#C41E3A',
                  lineHeight: '1',
                  fontFamily: "'Bebas Neue', sans-serif",
                  letterSpacing: '1px'
                }}>
                  {stats.rejected}
                </div>
                <div style={{ 
                  fontSize: window.innerWidth <= 768 ? '0.7rem' : '0.85rem', 
                  fontWeight: '600', 
                  color: '#9CA3AF',
                  textTransform: 'uppercase', 
                  letterSpacing: '0.5px',
                  marginTop: '0.5rem'
                }}>
                  Rejected
                </div>
              </div>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(196, 30, 58, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C41E3A" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Registrations Table */}
        <div style={{ 
          background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)',
          borderRadius: '20px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(212, 175, 55, 0.15)',
          overflow: 'hidden',
          marginBottom: '2rem',
          animation: 'fadeInUp 0.6s ease-out 0.5s both'
        }}>
          <div style={{ 
            background: 'linear-gradient(135deg, rgba(10, 14, 23, 0.95) 0%, rgba(17, 24, 39, 0.9) 100%)',
            padding: '1.5rem 2rem',
            borderBottom: '1px solid rgba(212, 175, 55, 0.15)'
          }}>
            <h3 style={{ 
              fontSize: '1.5rem', 
              fontWeight: '700', 
              fontFamily: "'Bebas Neue', sans-serif",
              letterSpacing: '1px',
              textTransform: 'uppercase',
              background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FFD700" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              Student Registrations
            </h3>

            {/* Mobile Search and Filter - Positioned in header */}
            <div className="mobile-filters-button" style={{ marginTop: '1rem' }}>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Search students..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    border: '2px solid rgba(212, 175, 55, 0.3)',
                    borderRadius: '10px',
                    fontSize: '0.95rem',
                    background: 'rgba(26, 31, 46, 0.8)',
                    color: '#ffffff',
                    outline: 'none'
                  }}
                />
                <button
                  onClick={() => setShowMobileFilters(true)}
                  style={{
                    padding: '0.75rem 1rem',
                    background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                    color: '#0A0E17',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '0.85rem',
                    fontWeight: '700',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <svg 
                    width="14" 
                    height="14" 
                    viewBox="0 0 24 24" 
                    fill="currentColor" 
                    style={{ marginRight: '6px', verticalAlign: 'middle' }}
                  >
                    <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"/>
                  </svg>
                  Filters
                </button>
              </div>
              
              {/* Active Filters Display */}
              {(eventFilter !== 'all' || ageGroupFilter !== 'all' || statusFilter !== 'all') && (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                  {eventFilter !== 'all' && (
                    <span style={{ padding: '0.375rem 0.75rem', background: 'rgba(212, 175, 55, 0.15)', color: '#FFD700', fontSize: '0.75rem', borderRadius: '6px', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
                      Event: {eventFilter}
                    </span>
                  )}
                  {ageGroupFilter !== 'all' && (
                    <span style={{ padding: '0.375rem 0.75rem', background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6', fontSize: '0.75rem', borderRadius: '6px', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                      Age: {ageGroupFilter}
                    </span>
                  )}
                  {statusFilter !== 'all' && (
                    <span style={{ padding: '0.375rem 0.75rem', background: 'rgba(46, 139, 87, 0.15)', color: '#2E8B57', fontSize: '0.75rem', borderRadius: '6px', border: '1px solid rgba(46, 139, 87, 0.3)' }}>
                      Status: {statusFilter}
                    </span>
                  )}
                  <button
                    onClick={clearAllFilters}
                    style={{ 
                      padding: '0.375rem 0.75rem', 
                      background: 'rgba(196, 30, 58, 0.15)', 
                      color: '#C41E3A', 
                      fontSize: '0.75rem', 
                      border: '1px solid rgba(196, 30, 58, 0.3)', 
                      borderRadius: '6px', 
                      cursor: 'pointer'
                    }}
                  >
                    Clear All
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Guidelines Button */}
            <div className="mobile-guidelines-btn" style={{ marginTop: '1rem' }}>
              <button
                onClick={() => setShowMobileGuidelines(true)}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(212, 175, 55, 0.05) 100%)',
                  color: '#FFD700',
                  border: '2px solid rgba(212, 175, 55, 0.4)',
                  borderRadius: '10px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s ease'
                }}
              >
                <svg 
                  width="14" 
                  height="14" 
                  viewBox="0 0 24 24" 
                  fill="currentColor" 
                  style={{ marginRight: '6px', verticalAlign: 'middle' }}
                >
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                </svg>
                Registration Guidelines
              </button>
            </div>
          </div>
          
          <div style={{ padding: '2rem' }}>
            {/* Search and Filter Controls */}
            <div style={{ 
              marginBottom: '2rem'
            }}>
              {/* Desktop Filters */}
              <div className="desktop-filters" style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                alignItems: 'end'
              }}>
                {/* Search Input */}
                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#D4AF37',
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Search Students
                  </label>
                  <input
                    type="text"
                    placeholder="Enter student name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      border: '2px solid rgba(212, 175, 55, 0.3)',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      transition: 'all 0.3s ease',
                      outline: 'none',
                      background: 'rgba(26, 31, 46, 0.8)',
                      color: '#ffffff',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#FFD700';
                      e.target.style.boxShadow = '0 0 0 3px rgba(212, 175, 55, 0.15)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(212, 175, 55, 0.3)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Event Filter */}
                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#D4AF37',
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Filter by Event
                  </label>
                  <select
                    value={eventFilter}
                    onChange={(e) => setEventFilter(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      border: '2px solid rgba(212, 175, 55, 0.3)',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      background: 'rgba(26, 31, 46, 0.8)',
                      color: '#ffffff',
                      outline: 'none',
                      cursor: 'pointer',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#FFD700';
                      e.target.style.boxShadow = '0 0 0 3px rgba(212, 175, 55, 0.15)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(212, 175, 55, 0.3)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="all">All Events</option>
                    <option value="100m">100m Sprint</option>
                    <option value="200m">200m Sprint</option>
                    <option value="400m">400m Sprint</option>
                    <option value="800m">800m Run</option>
                    <option value="1000m">1000m Run</option>
                    <option value="1500m">1500m Run</option>
                    <option value="long_jump">Long Jump</option>
                    <option value="high_jump">High Jump</option>
                    <option value="shot_put">Shot Put</option>
                    <option value="discus">Discus</option>
                    <option value="javelin">Javelin</option>
                    <option value="4x100_relay">4x100m Relay</option>
                    <option value="4x400_relay">4x400m Relay</option>
                  </select>
                </div>

                {/* Age Group Filter */}
                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#D4AF37',
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Filter by Age Group
                  </label>
                  <select
                    value={ageGroupFilter}
                    onChange={(e) => setAgeGroupFilter(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      border: '2px solid rgba(212, 175, 55, 0.3)',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      background: 'rgba(26, 31, 46, 0.8)',
                      color: '#ffffff',
                      outline: 'none',
                      cursor: 'pointer',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#FFD700';
                      e.target.style.boxShadow = '0 0 0 3px rgba(212, 175, 55, 0.15)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(212, 175, 55, 0.3)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="all" style={{ background: '#1a1f2e' }}>All Age Groups</option>
                    <option value="grade7" style={{ background: '#1a1f2e' }}>Grade 7</option>
                    <option value="under14" style={{ background: '#1a1f2e' }}>Under 14</option>
                    <option value="under16" style={{ background: '#1a1f2e' }}>Under 16</option>
                    <option value="under18" style={{ background: '#1a1f2e' }}>Under 18</option>
                    <option value="under20" style={{ background: '#1a1f2e' }}>Under 20</option>
                  </select>
                </div>

                {/* Status Filter */}
                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#D4AF37',
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Filter by Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      border: '2px solid rgba(212, 175, 55, 0.3)',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      background: 'rgba(26, 31, 46, 0.8)',
                      color: '#ffffff',
                      outline: 'none',
                      cursor: 'pointer',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#FFD700';
                      e.target.style.boxShadow = '0 0 0 3px rgba(212, 175, 55, 0.15)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(212, 175, 55, 0.3)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="all" style={{ background: '#1a1f2e' }}>All Statuses</option>
                    <option value="pending" style={{ background: '#1a1f2e' }}>Pending</option>
                    <option value="approved" style={{ background: '#1a1f2e' }}>Approved</option>
                    <option value="rejected" style={{ background: '#1a1f2e' }}>Rejected</option>
                  </select>
                </div>

                {/* Clear Filters Button */}
                <div>
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setEventFilter('all');
                      setAgeGroupFilter('all');
                      setStatusFilter('all');
                      setCurrentPage(1);
                    }}
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      background: 'transparent',
                      color: '#FFD700',
                      border: '2px solid rgba(212, 175, 55, 0.4)',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                    onMouseOver={(e) => {
                      (e.target as HTMLElement).style.background = 'rgba(212, 175, 55, 0.1)';
                      (e.target as HTMLElement).style.borderColor = '#FFD700';
                    }}
                    onMouseOut={(e) => {
                      (e.target as HTMLElement).style.background = 'transparent';
                      (e.target as HTMLElement).style.borderColor = 'rgba(212, 175, 55, 0.4)';
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>

            </div>

            {registrations.length === 0 ? (
              <div style={{ 
                textAlign: 'center',
                padding: '4rem 2rem',
                background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.05) 0%, rgba(196, 30, 58, 0.03) 100%)',
                borderRadius: '16px',
                border: '2px dashed rgba(212, 175, 55, 0.3)'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(196, 30, 58, 0.15) 0%, rgba(196, 30, 58, 0.1) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem auto',
                  border: '1px solid rgba(196, 30, 58, 0.3)'
                }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#C41E3A" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <line x1="23" y1="11" x2="17" y2="11"/>
                  </svg>
                </div>
                <h3 style={{ 
                  fontSize: '1.75rem', 
                  fontWeight: '700', 
                  fontFamily: "'Bebas Neue', sans-serif",
                  letterSpacing: '1px',
                  background: 'linear-gradient(135deg, #C41E3A 0%, #8B2942 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: '1rem'
                }}>
                  No Registrations Yet
                </h3>
                <p style={{ 
                  color: '#9CA3AF', 
                  marginBottom: '2rem',
                  fontSize: '1rem',
                  maxWidth: '400px',
                  margin: '0 auto 2rem auto',
                  lineHeight: '1.6'
                }}>
                  {isRegistrationOpen 
                    ? "Start by registering your first student athlete for track and field events."
                    : "Registration is currently closed. Please contact the administrator for more information."}
                </p>
                {isRegistrationOpen ? (
                  <Link 
                    to="/register" 
                    style={{ 
                      background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 50%, #D4AF37 100%)',
                      backgroundSize: '200% 200%',
                      color: '#0A0E17',
                      padding: '1rem 2.5rem',
                      borderRadius: '12px',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      fontSize: '1rem',
                      fontWeight: '700',
                      boxShadow: '0 4px 20px rgba(212, 175, 55, 0.3)',
                      transition: 'all 0.3s ease',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      e.currentTarget.style.boxShadow = '0 8px 30px rgba(212, 175, 55, 0.4)';
                      e.currentTarget.style.backgroundPosition = '100% 0';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 20px rgba(212, 175, 55, 0.3)';
                      e.currentTarget.style.backgroundPosition = '0 0';
                    }}
                  >
                    Register First Student
                  </Link>
                ) : (
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(107, 114, 128, 0.15) 0%, rgba(107, 114, 128, 0.1) 100%)',
                    color: '#9CA3AF',
                    padding: '1rem 2rem',
                    borderRadius: '12px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    border: '1px solid rgba(107, 114, 128, 0.3)'
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C41E3A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="15" y1="9" x2="9" y2="15"/>
                      <line x1="9" y1="9" x2="15" y2="15"/>
                    </svg>
                    Registration Closed
                  </div>
                )}
              </div>
            ) : (
              <div className="responsive-table" style={{ overflowX: 'auto' }}>
                {/* Pagination calculations */}
                {(() => {
                  const totalStudents = filteredStudents.length;
                  const totalPagesCalc = Math.ceil(totalStudents / itemsPerPage);
                  const startIndex = (currentPage - 1) * itemsPerPage;
                  const endIndex = startIndex + itemsPerPage;
                  const paginatedStudents = filteredStudents.slice(startIndex, endIndex);
                  
                  // Update total pages if different
                  if (totalPagesCalc !== totalPages) {
                    setTotalPages(totalPagesCalc);
                  }
                  
                  return (
                <>
                  {/* Desktop table */}
                <table style={{ 
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.95rem'
                }}>
                  <thead>
                    <tr style={{ 
                      background: 'linear-gradient(135deg, rgba(233, 77, 26, 0.02) 0%, rgba(233, 77, 26, 0.01) 100%)',
                      borderBottom: '2px solid #e5e7eb'
                    }}>
                      <th style={{ 
                        padding: '1rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.9rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>Student Name</th>
                      <th style={{ 
                        padding: '1rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.9rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>Events</th>
                      <th style={{ 
                        padding: '1rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.9rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>Age Group</th>
                      <th style={{ 
                        padding: '1rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.9rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>Gender</th>
                      <th style={{ 
                        padding: '1rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.9rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>Date of Birth</th>
                      <th style={{ 
                        padding: '1rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.9rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>Registration Date</th>
                      <th style={{ 
                        padding: '1rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.9rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>Status</th>
                      <th style={{ 
                        padding: '1rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.9rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedStudents.map((student, index) => (
                      <tr 
                        key={`${student.firstName}_${student.lastName}_${student.dateOfBirth}_${index}`}
                        style={{ 
                          borderBottom: '1px solid #f3f4f6',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <td style={{ 
                          padding: '1rem',
                          fontWeight: '600',
                          color: '#1f2937'
                        }}>
                          {student.firstName} {student.lastName}
                        </td>
                        <td style={{ 
                          padding: '1rem',
                          color: '#6b7280'
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {student.events.map((event, eventIndex) => (
                              <div key={eventIndex} style={{ 
                                padding: '4px 8px',
                                backgroundColor: '#f8fafc',
                                borderRadius: '6px',
                                fontSize: '0.875rem',
                                fontWeight: '500'
                              }}>
                                {event.eventName}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td style={{ 
                          padding: '1rem',
                          color: '#6b7280'
                        }}>
                          {getAgeGroupName(student.ageGroup || '')}
                        </td>
                        <td style={{ 
                          padding: '1rem',
                          color: '#6b7280',
                          textTransform: 'capitalize'
                        }}>
                          {student.gender}
                        </td>
                        <td style={{ 
                          padding: '1rem',
                          color: '#6b7280'
                        }}>
                          {formatDate(student.dateOfBirth || '')}
                        </td>
                        <td style={{ 
                          padding: '1rem',
                          color: '#6b7280'
                        }}>
                          {formatDate(student.registrationDate || '')}
                        </td>
                        <td style={{ 
                          padding: '1rem'
                        }}>
                          {/* Show status summary like "3/3 PENDING" or "2/3 APPROVED" */}
                          <span style={{
                            padding: '0.375rem 0.875rem',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            ...(() => {
                              const totalEvents = student.events.length;
                              const approvedCount = student.events.filter(e => e.status === 'approved').length;
                              const pendingCount = student.events.filter(e => e.status === 'pending').length;
                              const rejectedCount = student.events.filter(e => e.status === 'rejected').length;
                              
                              if (approvedCount === totalEvents) {
                                return { background: '#d1fae5', color: '#065f46' };
                              } else if (pendingCount > 0) {
                                return { background: '#fef3c7', color: '#92400e' };
                              } else if (rejectedCount > 0) {
                                return { background: '#fee2e2', color: '#991b1b' };
                              }
                              return { background: '#f3f4f6', color: '#6b7280' };
                            })()
                          }}>
                            {(() => {
                              const totalEvents = student.events.length;
                              const approvedCount = student.events.filter(e => e.status === 'approved').length;
                              const pendingCount = student.events.filter(e => e.status === 'pending').length;
                              const rejectedCount = student.events.filter(e => e.status === 'rejected').length;
                              
                              if (approvedCount === totalEvents) {
                                return `${approvedCount}/${totalEvents} APPROVED`;
                              } else if (pendingCount === totalEvents) {
                                return `${pendingCount}/${totalEvents} PENDING`;
                              } else if (rejectedCount === totalEvents) {
                                return `${rejectedCount}/${totalEvents} REJECTED`;
                              } else if (approvedCount > 0 && pendingCount > 0) {
                                return `${approvedCount}/${totalEvents} APPROVED`;
                              } else if (approvedCount > 0 && rejectedCount > 0) {
                                return `${approvedCount}/${totalEvents} APPROVED`;
                              } else if (pendingCount > 0 && rejectedCount > 0) {
                                return `${pendingCount}/${totalEvents} PENDING`;
                              }
                              return `${totalEvents}/${totalEvents} UNKNOWN`;
                            })()}
                          </span>
                        </td>
                        <td style={{ 
                          padding: '1rem'
                        }}>
                          <button
                            onClick={() => handleEditStudent(student)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              fontSize: '0.8rem',
                              fontWeight: '500',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.background = '#2563eb';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.3)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.background = '#3b82f6';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.2)';
                            }}
                            title="Edit student registrations"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* Mobile cards */}
                <div className="mobile-cards" style={{ display: 'none' }}>
                  {paginatedStudents.map((student, index) => (
                    <div key={`${student.firstName}_${student.lastName}_${student.dateOfBirth}_${index}`} className="student-card">
                      <div className="student-card-header">
                        <div className="student-name">
                          {student.firstName} {student.lastName}
                        </div>
                        <MobileActionsMenu 
                          student={student} 
                          onEdit={handleEditStudent}
                        />
                      </div>
                      
                      <div className="card-info-grid">
                        <div className="card-info-item">
                          <div className="card-info-label">Age Group</div>
                          <div className="card-info-value">{getAgeGroupName(student.ageGroup || '')}</div>
                        </div>
                        <div className="card-info-item">
                          <div className="card-info-label">Gender</div>
                          <div className="card-info-value" style={{ textTransform: 'capitalize' }}>{student.gender}</div>
                        </div>
                        <div className="card-info-item">
                          <div className="card-info-label">Date of Birth</div>
                          <div className="card-info-value">{formatDate(student.dateOfBirth || '')}</div>
                        </div>
                        <div className="card-info-item">
                          <div className="card-info-label">Reg. Date</div>
                          <div className="card-info-value">{formatDate(student.registrationDate || '')}</div>
                        </div>
                      </div>
                      
                      <div className="events-list">
                        <div className="events-label">Events ({student.events.length}/3)</div>
                        <div className="event-tags">
                          {student.events.map((event, eventIndex) => (
                            <span key={eventIndex} className="event-tag">
                              {event.eventName}
                            </span>
                          ))}
                        </div>
                        
                        {/* Status badge */}
                        <div style={{ marginTop: '0.75rem' }}>
                          <span style={{
                            padding: '0.375rem 0.875rem',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            ...(() => {
                              const totalEvents = student.events.length;
                              const approvedCount = student.events.filter(e => e.status === 'approved').length;
                              const pendingCount = student.events.filter(e => e.status === 'pending').length;
                              const rejectedCount = student.events.filter(e => e.status === 'rejected').length;
                              
                              if (approvedCount === totalEvents) {
                                return { background: '#d1fae5', color: '#065f46' };
                              } else if (pendingCount > 0) {
                                return { background: '#fef3c7', color: '#92400e' };
                              } else if (rejectedCount > 0) {
                                return { background: '#fee2e2', color: '#991b1b' };
                              }
                              return { background: '#f3f4f6', color: '#6b7280' };
                            })()
                          }}>
                            {(() => {
                              const totalEvents = student.events.length;
                              const approvedCount = student.events.filter(e => e.status === 'approved').length;
                              const pendingCount = student.events.filter(e => e.status === 'pending').length;
                              const rejectedCount = student.events.filter(e => e.status === 'rejected').length;
                              
                              if (approvedCount === totalEvents) {
                                return `${approvedCount}/${totalEvents} APPROVED`;
                              } else if (pendingCount === totalEvents) {
                                return `${pendingCount}/${totalEvents} PENDING`;
                              } else if (rejectedCount === totalEvents) {
                                return `${rejectedCount}/${totalEvents} REJECTED`;
                              } else if (approvedCount > 0 && pendingCount > 0) {
                                return `${approvedCount}/${totalEvents} APPROVED`;
                              } else if (approvedCount > 0 && rejectedCount > 0) {
                                return `${approvedCount}/${totalEvents} APPROVED`;
                              } else if (pendingCount > 0 && rejectedCount > 0) {
                                return `${pendingCount}/${totalEvents} PENDING`;
                              }
                              return `${totalEvents}/${totalEvents} UNKNOWN`;
                            })()}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                </>
                );
                })()}
                
                {/* Pagination Controls */}
                {filteredStudents.length > itemsPerPage && (
                  <div style={{ 
                    marginTop: '2rem',
                    padding: '1rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'linear-gradient(135deg, rgba(233, 77, 26, 0.03) 0%, rgba(233, 77, 26, 0.01) 100%)',
                    borderRadius: '12px',
                    border: '1px solid rgba(233, 77, 26, 0.1)'
                  }}>
                    <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                      Page {currentPage} of {totalPages} ({filteredStudents.length} students)
                    </div>
                    
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        style={{
                          padding: '0.5rem 1rem',
                          background: currentPage === 1 ? '#f3f4f6' : 'linear-gradient(135deg, #e94d1a 0%, #ea580c 100%)',
                          color: currentPage === 1 ? '#9ca3af' : 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.9rem',
                          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Previous
                      </button>
                      
                      {/* Page Numbers */}
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            style={{
                              padding: '0.5rem 0.75rem',
                              background: page === currentPage 
                                ? 'linear-gradient(135deg, #e94d1a 0%, #ea580c 100%)' 
                                : 'white',
                              color: page === currentPage ? 'white' : '#374151',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              fontSize: '0.9rem',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                              if (page !== currentPage) {
                                (e.target as HTMLElement).style.background = '#f3f4f6';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (page !== currentPage) {
                                (e.target as HTMLElement).style.background = 'white';
                              }
                            }}
                          >
                            {page}
                          </button>
                        ))}
                      </div>
                      
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        style={{
                          padding: '0.5rem 1rem',
                          background: currentPage === totalPages ? '#f3f4f6' : 'linear-gradient(135deg, #e94d1a 0%, #ea580c 100%)',
                          color: currentPage === totalPages ? '#9ca3af' : 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.9rem',
                          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Registration Guidelines */}
        <div className="desktop-guidelines" style={{ 
          background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)',
          borderRadius: '20px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(212, 175, 55, 0.15)',
          overflow: 'hidden',
          animation: 'fadeInUp 0.6s ease-out 0.6s both'
        }}>
          <div style={{ 
            background: 'linear-gradient(135deg, rgba(10, 14, 23, 0.95) 0%, rgba(17, 24, 39, 0.9) 100%)',
            padding: '1.5rem 2rem',
            borderBottom: '1px solid rgba(212, 175, 55, 0.15)'
          }}>
            <h3 style={{ 
              fontSize: '1.5rem', 
              fontWeight: '700', 
              fontFamily: "'Bebas Neue', sans-serif",
              letterSpacing: '1px',
              textTransform: 'uppercase',
              background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FFD700" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10,9 9,9 8,9"/>
              </svg>
              Registration Guidelines
            </h3>
          </div>
          
          <div style={{ padding: '2rem' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
              gap: '2rem' 
            }}>
              <div style={{ 
                padding: '1.5rem',
                background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.08) 0%, rgba(212, 175, 55, 0.03) 100%)',
                borderRadius: '16px',
                border: '1px solid rgba(212, 175, 55, 0.2)',
                borderLeft: '4px solid #FFD700'
              }}>
                <h4 style={{ 
                  color: '#FFD700', 
                  marginBottom: '1rem',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FFD700" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  Important Rules
                </h4>
                <ul style={{ 
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  color: '#D4AF37'
                }}>
                  <li style={{ 
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#FFD700', fontWeight: 'bold' }}>•</span>
                    Each student can register for maximum 3 events
                  </li>
                  <li style={{ 
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#FFD700', fontWeight: 'bold' }}>•</span>
                    Maximum 3 students per event per age/gender category
                  </li>
                  <li style={{ 
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#FFD700', fontWeight: 'bold' }}>•</span>
                    Student age must match the selected age group
                  </li>
                  <li style={{ 
                    marginBottom: '0',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#FFD700', fontWeight: 'bold' }}>•</span>
                    Only pending registrations can be edited or deleted
                  </li>
                </ul>
              </div>
              
              <div style={{ 
                padding: '1.5rem',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.03) 100%)',
                borderRadius: '16px',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderLeft: '4px solid #3b82f6'
              }}>
                <h4 style={{ 
                  color: '#3b82f6', 
                  marginBottom: '1rem',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                    <polyline points="9,11 12,14 22,4"/>
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                  </svg>
                  Next Steps
                </h4>
                <ul style={{ 
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  color: '#60a5fa'
                }}>
                  <li style={{ 
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>•</span>
                    Submitted registrations will be reviewed by Headteacher
                  </li>
                  <li style={{ 
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>•</span>
                    Approved registrations cannot be modified
                  </li>
                  <li style={{ 
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>•</span>
                    Rejected registrations can be removed from the list
                  </li>
                  <li style={{ 
                    marginBottom: '0',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>•</span>
                    Check the Reports section for detailed analytics
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Student Dialog */}
      {showEditDialog && selectedStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            border: '2px solid #3b82f6'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem',
              paddingBottom: '1rem',
              borderBottom: '2px solid #f3f4f6'
            }}>
              <h2 style={{
                color: '#1f2937',
                fontSize: '1.5rem',
                fontWeight: '700',
                margin: 0
              }}>
                Edit Student Registration
              </h2>
              <button
                onClick={handleCloseEditDialog}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '0.5rem',
                  borderRadius: '8px'
                }}
                onMouseOver={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#f3f4f6'}
                onMouseOut={(e) => (e.target as HTMLButtonElement).style.backgroundColor = 'transparent'}
              >
                ×
              </button>
            </div>

            {/* Student Info */}
            <div style={{
              backgroundColor: '#f8fafc',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{
                fontSize: '1.1rem',
                fontWeight: '600',
                color: '#374151',
                margin: '0 0 0.5rem 0'
              }}>
                Student Information
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.9rem' }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontWeight: 'bold', 
                    marginBottom: '0.25rem',
                    color: '#374151' 
                  }}>
                    First Name:
                  </label>
                  <input
                    type="text"
                    value={editedFirstName}
                    onChange={(e) => setEditedFirstName(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '0.9rem'
                    }}
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontWeight: 'bold', 
                    marginBottom: '0.25rem',
                    color: '#374151' 
                  }}>
                    Last Name:
                  </label>
                  <input
                    type="text"
                    value={editedLastName}
                    onChange={(e) => setEditedLastName(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '0.9rem'
                    }}
                  />
                </div>
                <div>
                  <strong>Gender:</strong> {selectedStudent.gender}
                </div>
                <div>
                  <strong>Date of Birth:</strong> {formatDate(selectedStudent.dateOfBirth)}
                </div>
                <div>
                  <strong>Age Group:</strong> {getAgeGroupName(selectedStudent.ageGroup)}
                </div>
              </div>
            </div>

            {/* Events List */}
            <div>
              <h3 style={{
                fontSize: '1.1rem',
                fontWeight: '600',
                color: '#374151',
                margin: '0 0 1rem 0'
              }}>
                Registered Events
              </h3>
              
              {/* Grade 7 Information Message */}
              {selectedStudent.ageGroup === 'Grade 7' && (
                <div style={{
                  padding: '1rem',
                  marginBottom: '1rem',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  color: '#92400e'
                }}>
                  <strong>Grade 7 Events:</strong> Individual events cannot be removed as all Grade 7 events are compulsory. 
                  To remove this student, use the "Delete Student" button below.
                </div>
              )}

              {/* Reviewed Student Information Message */}
              {hasBeenReviewed(selectedStudent) && (
                <div style={{
                  padding: '1rem',
                  marginBottom: '1rem',
                  backgroundColor: '#e0f2fe',
                  border: '1px solid #0284c7',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  color: '#0c4a6e'
                }}>
                  <strong>Student Reviewed:</strong> This student's registrations have been reviewed by the headmaster. 
                  Events can no longer be added or removed by coaches.
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {selectedStudent.events.map((event, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1rem',
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: '#1f2937',
                        marginBottom: '0.25rem'
                      }}>
                        {event.eventName}
                      </div>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.03em',
                        ...(event.status === 'approved' ? {
                          background: '#d1fae5',
                          color: '#065f46'
                        } : event.status === 'pending' ? {
                          background: '#fef3c7',
                          color: '#92400e'
                        } : {
                          background: '#fee2e2',
                          color: '#991b1b'
                        })
                      }}>
                        {event.status}
                      </span>
                    </div>
                    
                    {(event.status === 'pending' || event.status === 'rejected') && 
                     selectedStudent.ageGroup !== 'Grade 7' && 
                     !hasBeenReviewed(selectedStudent) && (
                      <button
                        onClick={() => handleDeleteRegistration(event.id, event.eventName)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.background = '#dc2626';
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = '#ef4444';
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.2)';
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                
                {/* Add Event Tile for non-Grade 7 students with fewer than 3 events who haven't been reviewed */}
                {selectedStudent.ageGroup !== 'Grade 7' && 
                 selectedStudent.events.length < 3 && 
                 !hasBeenReviewed(selectedStudent) && (
                  <div 
                    onClick={() => handleAddEvent(selectedStudent)}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '2rem',
                      backgroundColor: '#f9fafb',
                      border: '2px dashed #d1d5db',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      minHeight: '80px'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                      e.currentTarget.style.borderColor = '#9ca3af';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                      e.currentTarget.style.borderColor = '#d1d5db';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: '#6b7280'
                    }}>
                      <div style={{
                        fontSize: '2rem',
                        fontWeight: 'bold',
                        color: '#4b5563'
                      }}>
                        +
                      </div>
                      <div style={{
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        color: '#4b5563'
                      }}>
                        Add Event
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={{
              display: 'flex',
              justifyContent: selectedStudent.ageGroup === 'Grade 7' ? 'space-between' : 'flex-end',
              gap: '1rem',
              marginTop: '2rem',
              paddingTop: '1rem',
              borderTop: '1px solid #e5e7eb'
            }}>
              {/* Grade 7 Delete Student Button */}
              {selectedStudent.ageGroup === 'Grade 7' && (
                <button
                  onClick={() => handleDeleteStudent(selectedStudent)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#dc2626';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = '#ef4444';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.2)';
                  }}
                >
                  Delete Student
                </button>
              )}
              
              {/* Save Name Button */}
              <button
                onClick={handleSaveStudentName}
                disabled={isUpdatingName || !editedFirstName.trim() || !editedLastName.trim()}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: isUpdatingName || !editedFirstName.trim() || !editedLastName.trim() 
                    ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: isUpdatingName || !editedFirstName.trim() || !editedLastName.trim() 
                    ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)'
                }}
                onMouseOver={(e) => {
                  if (!isUpdatingName && editedFirstName.trim() && editedLastName.trim()) {
                    e.currentTarget.style.background = '#059669';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.3)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isUpdatingName && editedFirstName.trim() && editedLastName.trim()) {
                    e.currentTarget.style.background = '#10b981';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.2)';
                  }
                }}
              >
                {isUpdatingName ? 'Saving...' : 'Save Name'}
              </button>
              
              {/* Close Button */}
              <button
                onClick={handleCloseEditDialog}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#2563eb';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = '#3b82f6';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.2)';
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        title="CONFIRM REMOVAL"
        message={`Are you sure you want to remove "${eventToDelete}" from the student's registration?`}
        confirmText="OK"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        type="danger"
      />

      {/* Student Deletion Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showStudentDeleteDialog}
        title="DELETE GRADE 7 STUDENT"
        message={studentToDelete ? `Are you sure you want to remove ${studentToDelete.firstName} ${studentToDelete.lastName} from ALL events? This will remove the student from all Grade 7 events as they are compulsory.` : ''}
        details={studentToDelete ? [
          `Student: ${studentToDelete.firstName} ${studentToDelete.lastName}`,
          `Age Group: Grade 7`,
          `Events: ${studentToDelete.events.length} event${studentToDelete.events.length !== 1 ? 's' : ''}`,
          'All events will be removed as Grade 7 events are compulsory'
        ] : []}
        confirmText="DELETE STUDENT"
        cancelText="Cancel"
        onConfirm={confirmStudentDelete}
        onCancel={cancelStudentDelete}
        type="danger"
      />

      {/* Add Event Dialog */}
      {showAddEventDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: '#1f2937',
                margin: 0
              }}>
                Add Event
              </h2>
              <button
                onClick={() => setShowAddEventDialog(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '0.25rem'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{
                color: '#4b5563',
                fontSize: '0.9rem',
                margin: '0 0 1rem 0'
              }}>
                Select an event to add for {studentForEventAdd?.firstName} {studentForEventAdd?.lastName}:
              </p>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              maxHeight: '300px',
              overflow: 'auto'
            }}>
              {availableEvents.length === 0 ? (
                <div style={{
                  padding: '2rem',
                  textAlign: 'center',
                  color: '#6b7280',
                  fontStyle: 'italic'
                }}>
                  No available events for this student's age group and gender.
                </div>
              ) : (
                availableEvents.map(event => (
                  <div
                    key={event.id}
                    onClick={() => handleConfirmAddEvent(event.id)}
                    style={{
                      padding: '1rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'white'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                      e.currentTarget.style.borderColor = '#3b82f6';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '0.5rem'
                    }}>
                      <div style={{
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: '#1f2937'
                      }}>
                        {event.name}
                      </div>
                      <div style={{
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        color: '#059669',
                        backgroundColor: '#d1fae5',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '12px',
                        whiteSpace: 'nowrap',
                        marginLeft: '1rem'
                      }}>
                        {event.registrationCount}/3 registered
                      </div>
                    </div>
                    <div style={{
                      fontSize: '0.8rem',
                      color: '#6b7280'
                    }}>
                      {event.category} • {event.description}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '2rem',
              paddingTop: '1rem',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={() => setShowAddEventDialog(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#4b5563';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = '#6b7280';
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Filters Modal */}
      {showMobileFilters && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px 12px 0 0',
            padding: '1.5rem',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            animation: 'slideUp 0.3s ease-out'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem',
              paddingBottom: '1rem',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '1.2rem',
                fontWeight: 'bold',
                color: '#1f2937',
                margin: 0
              }}>
                Filter Students
              </h3>
              <button
                onClick={handleMobileFiltersClose}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '0.25rem'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Event Filter */}
              <div>
                <label style={{ 
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.9rem'
                }}>
                  Filter by Event
                </label>
                <select
                  value={eventFilter}
                  onChange={(e) => setEventFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    background: 'white'
                  }}
                >
                  <option value="all">All Events</option>
                  <option value="100m">100m Sprint</option>
                  <option value="200m">200m Sprint</option>
                  <option value="400m">400m Sprint</option>
                  <option value="800m">800m Run</option>
                  <option value="1000m">1000m Run</option>
                  <option value="1500m">1500m Run</option>
                  <option value="long_jump">Long Jump</option>
                  <option value="high_jump">High Jump</option>
                  <option value="shot_put">Shot Put</option>
                  <option value="discus">Discus</option>
                  <option value="javelin">Javelin</option>
                  <option value="4x100_relay">4x100m Relay</option>
                  <option value="4x400_relay">4x400m Relay</option>
                </select>
              </div>

              {/* Age Group Filter */}
              <div>
                <label style={{ 
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.9rem'
                }}>
                  Filter by Age Group
                </label>
                <select
                  value={ageGroupFilter}
                  onChange={(e) => setAgeGroupFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    background: 'white'
                  }}
                >
                  <option value="all">All Age Groups</option>
                  <option value="grade7">Grade 7</option>
                  <option value="under14">Under 14</option>
                  <option value="under16">Under 16</option>
                  <option value="under18">Under 18</option>
                  <option value="under20">Under 20</option>
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label style={{ 
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.9rem'
                }}>
                  Filter by Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    background: 'white'
                  }}
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  onClick={clearAllFilters}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Clear All
                </button>
                <button
                  onClick={handleMobileFiltersClose}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: '#e94d1a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Guidelines Modal */}
      {showMobileGuidelines && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px 12px 0 0',
            padding: '1.5rem',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            animation: 'slideUp 0.3s ease-out'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem',
              paddingBottom: '1rem',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '1.2rem',
                fontWeight: 'bold',
                color: '#1f2937',
                margin: 0
              }}>
                Registration Guidelines
              </h3>
              <button
                onClick={handleMobileGuidelinesClose}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '0.25rem'
                }}
              >
                ×
              </button>
            </div>

            {/* Important Rules Section */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ 
                padding: '1rem',
                background: 'linear-gradient(135deg, rgba(233, 77, 26, 0.03) 0%, white 100%)',
                borderRadius: '12px',
                border: '1px solid rgba(245, 158, 11, 0.2)',
                borderLeft: '4px solid #f59e0b'
              }}>
                <h4 style={{ 
                  color: '#92400e', 
                  marginBottom: '1rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  Important Rules
                </h4>
                <ul style={{ 
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  color: '#78350f'
                }}>
                  <li style={{ 
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5',
                    fontSize: '0.9rem'
                  }}>
                    <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>•</span>
                    Each student can register for maximum 3 events
                  </li>
                  <li style={{ 
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5',
                    fontSize: '0.9rem'
                  }}>
                    <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>•</span>
                    Maximum 3 students per event per age/gender category
                  </li>
                  <li style={{ 
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5',
                    fontSize: '0.9rem'
                  }}>
                    <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>•</span>
                    Student age must match the selected age group
                  </li>
                  <li style={{ 
                    marginBottom: '0',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5',
                    fontSize: '0.9rem'
                  }}>
                    <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>•</span>
                    Only pending registrations can be edited or deleted
                  </li>
                </ul>
              </div>
            </div>

            {/* Next Steps Section */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ 
                padding: '1rem',
                background: 'linear-gradient(135deg, rgba(233, 77, 26, 0.02) 0%, white 100%)',
                borderRadius: '12px',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderLeft: '4px solid #3b82f6'
              }}>
                <h4 style={{ 
                  color: '#1e40af', 
                  marginBottom: '1rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  Next Steps
                </h4>
                <ul style={{ 
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  color: '#1e3a8a'
                }}>
                  <li style={{ 
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5',
                    fontSize: '0.9rem'
                  }}>
                    <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>•</span>
                    Submitted registrations will be reviewed by Headteacher
                  </li>
                  <li style={{ 
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5',
                    fontSize: '0.9rem'
                  }}>
                    <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>•</span>
                    Approved registrations cannot be modified
                  </li>
                  <li style={{ 
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5',
                    fontSize: '0.9rem'
                  }}>
                    <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>•</span>
                    Rejected registrations can be removed from the list
                  </li>
                  <li style={{ 
                    marginBottom: '0',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5',
                    fontSize: '0.9rem'
                  }}>
                    <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>•</span>
                    Check the Reports section for detailed analytics
                  </li>
                </ul>
              </div>
            </div>

            {/* Close Button */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
              <button
                onClick={handleMobileGuidelinesClose}
                style={{
                  padding: '0.75rem 2rem',
                  background: '#e94d1a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

// Mobile Actions Menu Component
interface MobileActionsMenuProps {
  student: GroupedStudent;
  onEdit: (student: GroupedStudent) => void;
}

const MobileActionsMenu: React.FC<MobileActionsMenuProps> = ({ student, onEdit }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="actions-menu">
      <button 
        className="menu-button"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        ⋯
      </button>
      
      {isOpen && (
        <>
          {/* Backdrop to close menu when clicking outside */}
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 5
            }}
            onClick={() => setIsOpen(false)}
          />
          
          <div className="menu-dropdown">
            <button 
              className="menu-item"
              onClick={() => {
                onEdit(student);
                setIsOpen(false);
              }}
            >
              Edit Student
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CoachDashboard;
